import { BeneficiaryFilterItem } from '@repo/shared/dtos/beneficiary'
import { StructuredDataSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/command'
import { useT } from '@repo/ui/hooks'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAvailableSchemaItems } from '@/store/beneficiary-list-filters/selectors'
import { addFilter } from '@/store/beneficiary-list-filters/slice'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

import NewFilterForm from './filters/NewFilterForm'

export function AddFilterWrapper() {
  const dispatch = useStoreDispatch()
  const [open, setOpen] = useState(false)

  const loading = useStoreState((state) => state.beneficiaryList.loading)

  const availableSchemaItems = useSelector(selectAvailableSchemaItems)

  const onAdd = (filter: BeneficiaryFilterItem) => {
    setOpen(false)
    dispatch(addFilter(filter))
  }

  const disabled = loading || availableSchemaItems.length === 0

  return (
    <AddFilter
      open={open}
      setOpen={setOpen}
      disabled={disabled}
      schemaItems={availableSchemaItems}
      onAdd={onAdd}
    />
  )
}

export function AddFilter({
  open,
  setOpen,
  disabled,
  schemaItems,
  onAdd,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  disabled: boolean
  schemaItems: StructuredDataSchemaItem[]
  onAdd: (filter: BeneficiaryFilterItem) => void
}) {
  const { t } = useT({ keyPrefix: 'beneficiary.filters' })
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <TooltipWrapper content={t('addFirstFilter', { cfl: true })}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            size="icon"
            className="data-[state=open]:bg-opacity-90"
          >
            <PlusIcon className="size-4" />
          </Button>
        </PopoverTrigger>
      </TooltipWrapper>
      <PopoverContent align="start" className="overflow-y-hidden p-0">
        <Command>
          <CommandInput placeholder={t('searchPlaceholder', { cfl: true })} className="h-9" />
          <CommandList>
            <CommandEmpty>{t('none', { cfl: true })}</CommandEmpty>
            <CommandGroup>
              {schemaItems.map((schemaItem) => (
                <CommandItem key={schemaItem.id}>
                  <NewFilterForm schemaItem={schemaItem} onAdd={onAdd} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
